import * as React from "react"
import { router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Select } from "@/components/ui/select"
import type { ClipTypeRef } from "@/components/clips/types"

export function ClipUploadDialog({
  open,
  onOpenChange,
  brollRootPath,
  clipTypes = [],
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  brollRootPath: string | null
  clipTypes?: ClipTypeRef[]
}) {
  const [file, setFile] = React.useState<File | null>(null)
  const [clipTypeId, setClipTypeId] = React.useState<string>("")
  const [submitting, setSubmitting] = React.useState(false)

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!file) return

    setSubmitting(true)
    const data = new FormData()
    data.append("file", file)
    if (clipTypeId) data.append("clip_type_id", clipTypeId)
    router.post("/clips/upload", data, {
      forceFormData: true,
      onFinish: () => {
        setSubmitting(false)
        setFile(null)
        setClipTypeId("")
        onOpenChange(false)
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload a clip</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-ink-muted">
            The file will be uploaded to an <code>_inbox/</code> in{" "}
            <code>{brollRootPath}</code> and picked up by the next sync. Picking a clip type sends it
            to that type's own inbox.
          </p>
          <input
            type="file"
            accept="video/mp4,video/quicktime,video/x-m4v,video/webm"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-ink-body"
          />
          {clipTypes.length > 0 && (
            <div>
              <label htmlFor="upload-clip-type">Clip type</label>
              <Select
                id="upload-clip-type"
                value={clipTypeId}
                onChange={(e) => setClipTypeId(e.target.value)}
                className="mt-1 w-full"
              >
                <option value="">None — root _inbox/</option>
                {clipTypes.map((clipType) => (
                  <option key={clipType.id} value={String(clipType.id)}>
                    {clipType.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
          {file && (
            <p className="text-xs text-ink-muted">
              {file.name} — {(file.size / 1024 / 1024).toFixed(1)} MB
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={!file || submitting}>
              {submitting ? "Uploading…" : "Upload"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
